/**
 * QuickBookingModal
 * -----------------------------------------------------------------------------
 * A non-blocking "take a phone call → create a booking" popup that can be
 * opened from anywhere in the app (Workshop Pulse widget, Bookings page, etc.)
 * without interrupting the user's current work (e.g. an invoice being edited
 * doesn't lose state — the modal lives ABOVE the page).
 *
 * Flow:
 *   1. SM searches an existing customer (debounced /api/bookings/search/customers
 *      which returns the customer's stored vehicles).
 *   2. SM either selects an existing customer + vehicle, OR
 *      types a brand-new customer + vehicle inline.
 *   3. SM picks scheduled date/time, service types, optional loan-car request.
 *   4. POST /api/bookings → success toast → modal closes → onCreated fires
 *      so the caller can refresh its list (or not).
 *
 * The backend already auto-creates a customer / vehicle when only typed-in
 * details are provided, and the booking flows through to a job-card on the
 * scheduled date via existing flows.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  PhoneIncoming,
  Search,
  Car,
  User,
  Calendar,
  Save,
  X,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { melbourneISO } from '@/lib/melbourneTime';
import { formatAUMobile, formatRego } from '@/lib/formatters';
import QuoteBuilder, { buildQuoteSmsBody } from '@/components/QuoteBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'service', label: 'Service' },
  { value: 'rwc', label: 'RWC' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
  // iter129 — Phone-call Quote. When this is the ONLY type selected, the
  // booking form switches into quote-mode: no date/time required, a
  // QuoteBuilder appears in the notes area, and the bottom button changes
  // to "Send Quote (no booking)".
  { value: 'quote', label: 'Quote (phone)', quoteOnly: true },
];

const todayPlus = (days = 1) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  // Local YYYY-MM-DD (avoid timezone drift from .toISOString())
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const blankForm = () => ({
  // Selected entities (filled when SM picks from search results)
  customer_id: null,
  vehicle_id: null,
  // Editable fields — full customer identity so the booking popup can
  // capture a brand-new customer's complete profile in one shot
  // (matches the JC modal's Edit Customer Details dialog).
  customer_name: '',
  first_name: '',
  last_name: '',
  customer_phone: '',
  customer_landline: '',
  customer_email: '',
  address_line1: '',
  address_line2: '',
  suburb: '',
  state: '',
  postcode: '',
  company: '',
  abn: '',
  vehicle_rego: '',
  vehicle_make: '',
  vehicle_model: '',
  // Scheduling
  scheduled_date: todayPlus(1),
  scheduled_time: '09:00',
  // iter129 — Don't pre-select a service type. SM must explicitly tick
  // which type(s) this booking is for, so the form fails validation if
  // they forget. (Previously defaulted to ['service'] which silently
  // tagged every booking — even Mech/Tyres-only jobs — as a "service".)
  service_types: [],
  loan_car_requested: false,
  customer_license_number: '',
  notes: '',
});

// "Outstanding work from previous visits" picker — surfaces the items
// the SM previously quoted (or the mechanic flagged) on this vehicle that
// were never actioned. Ticking a row carries it (with full pricing
// snapshot) onto the new JC so the mechanic has a pre-loaded task list.
function PendingWorkPicker({ loading, items, selected, onToggle, onToggleAll }) {
  const total = items.length;
  const selectedCount = items.filter(it => selected[it.pending_id]).length;
  if (loading) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="qb-pending-loading">
        Checking previous visits for unresolved work…
      </div>
    );
  }
  if (total === 0) {
    return (
      <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700" data-testid="qb-pending-empty">
        ✓ No outstanding work from previous visits on this vehicle.
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-lg border-2 border-amber-200 bg-amber-50/60" data-testid="qb-pending-picker">
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-200 bg-amber-100/50">
        <div className="text-xs font-semibold text-amber-900 flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{total}</span>
          Outstanding work from previous visits
          <span className="text-[10px] font-normal text-amber-700">· tick what the customer wants done</span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => onToggleAll(true)}
            className="text-amber-800 hover:text-amber-950 underline"
            data-testid="qb-pending-select-all"
          >
            Select all
          </button>
          <span className="text-amber-400">·</span>
          <button
            type="button"
            onClick={() => onToggleAll(false)}
            className="text-amber-800 hover:text-amber-950 underline"
            data-testid="qb-pending-clear"
          >
            Clear
          </button>
          <span className="ml-1 text-amber-900 font-semibold">{selectedCount}/{total}</span>
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-amber-100">
        {items.map((it) => {
          const ticked = !!selected[it.pending_id];
          const hasPricing = it.parts_price != null || it.labour_hours != null;
          // Heuristic per-row price for the SM to see what was last quoted.
          const lineTotal = it.line_total
            || (it.parts_price != null || it.labour_hours != null
                ? ((it.parts_price || 0) * (1 + (it.parts_margin_pct || 0) / 100)
                   + (it.labour_hours || 0) * (it.labour_rate || 0))
                : null);
          return (
            <label
              key={it.pending_id}
              className={`flex items-start gap-3 px-3 py-2 cursor-pointer transition ${ticked ? 'bg-amber-100/40' : 'hover:bg-amber-50'}`}
              data-testid={`qb-pending-row-${it.pending_id}`}
            >
              <input
                type="checkbox"
                checked={ticked}
                onChange={() => onToggle(it.pending_id)}
                className="mt-1 h-4 w-4 accent-amber-600"
                data-testid={`qb-pending-checkbox-${it.pending_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-800 truncate">{it.description}</span>
                  {it.source === 'quote' && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-blue-100 text-blue-700">previously quoted</span>
                  )}
                  {it.source === 'finding' && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-purple-100 text-purple-700">mechanic finding</span>
                  )}
                  {it.severity && (
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                      it.severity === 'urgent' ? 'bg-rose-100 text-rose-700'
                        : it.severity === 'high' ? 'bg-orange-100 text-orange-700'
                        : it.severity === 'medium' ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>{it.severity}</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-3 mt-0.5">
                  {it.source_invoice_no && <span>Last quoted: <span className="font-mono">{it.source_invoice_no}</span></span>}
                  {it.part_number && <span>Part: <span className="font-mono">{it.part_number}</span></span>}
                  {it.parts_supplier && <span>Supplier: {it.parts_supplier}</span>}
                  {hasPricing && lineTotal != null && (
                    <span className="font-semibold text-emerald-700">${lineTotal.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function QuickBookingModal({ open, onClose, onCreated, getSiteParams, prefill, prefillPendingItems = null, hideSearch = false }) {
  const { guardComms, commsDialog } = useCommsGate();
  const [form, setForm] = useState(blankForm());
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Pending work for the picked vehicle — surfaces "things we quoted last
  // visit but the customer didn't approve" so the SM can tick which ones
  // the customer is now booking in to fix. Items carry the previous
  // pricing snapshot so when the SM eventually creates an invoice for
  // this booking, the quote builder pre-fills ORP / RRP / margin / labour
  // exactly as it was last quoted.
  const [pendingWork, setPendingWork] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  // Map of pending_id → boolean. Defaults to all-ticked when the list
  // first lands — most of the time the customer's call is precisely
  // about these items, so making the SM tick every box is busywork.
  const [pendingSelected, setPendingSelected] = useState({});

  // Duplicate-phone conflict — captured when the backend returns a 409
  // because the typed phone is already linked to a DIFFERENT customer.
  // The SM picks "Use existing customer" (auto-fills the form with the
  // existing record and re-submits) or cancels back to the form.
  const [phoneConflict, setPhoneConflict] = useState(null);

  // iter122: Send Quote SMS state — separate from saving so the SM can
  // do BOTH (send quote, then schedule the booking) without the modal
  // closing. `quoteResult` captures the result so the modal can show a
  // success banner inline until SM closes or schedules a booking.
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  // iter129 — Quote-mode state. When the SM ticks ONLY the "Quote (phone)"
  // service type, the booking form switches into a quote-builder UI. No
  // date/time required; the bottom button becomes "Preview & Send Quote".
  // Re-uses the existing `showQuotePreview` state below (line ~502).
  const [quoteRows, setQuoteRows] = useState([]);
  const [quoteModeSmsBody, setQuoteModeSmsBody] = useState('');
  const [creatingQuote, setCreatingQuote] = useState(false);
  const quoteMode = form.service_types.length === 1 && form.service_types[0] === 'quote';

  // Reset state every time the modal opens (so no stale data lingers).
  // When a `prefill` payload is provided (e.g. from the Customer Details
  // vehicle menu → Create Booking, OR from Invoice → Quote Builder →
  // Book Customer), seed the form with the customer + vehicle so the SM
  // lands on a one-click-save state.
  //
  // iter155 — When the caller passes only `customer_id` and/or
  // `vehicle_id` (Invoice/Quote path), the invoice object doesn't carry
  // the full customer/vehicle profile (no first_name / last_name /
  // landline / address / company / abn / make / model / year). Fetch
  // the full records off /api/customers/{id} and /api/vehicles/{id}
  // and merge the fields in so the popup shows the complete profile
  // rather than half-blank inputs.
  //
  // iter160 — CRITICAL FIX for infinite loop. Prior code depended on
  // `prefill` (an object literal from the parent). Parent re-renders
  // handed us a NEW object identity each frame → useEffect re-fired
  // → setForm → parent re-render → new prefill → loop. Symptom: the
  // preview app "stuck in a loop". Fix: depend only on the primitive
  // ID fields (`open` + `prefill?.customer_id` + `prefill?.vehicle_id`)
  // which are stable across renders. Non-ID prefill fields (name,
  // rego etc.) are still merged into the seed because we read
  // `prefillRef.current` at effect time.
  const prefillRef = useRef(prefill);
  useEffect(() => { prefillRef.current = prefill; }, [prefill]);
  const _prefillCustomerId = prefill?.customer_id || null;
  const _prefillVehicleId = prefill?.vehicle_id || null;

  useEffect(() => {
    if (!open) return;
    const currentPrefill = prefillRef.current;
    const seeded = currentPrefill ? { ...blankForm(), ...currentPrefill } : blankForm();
    setForm(seeded);
    setQuery('');
    setResults([]);
    setPendingWork([]);
    setPendingSelected({});
    setQuoteResult(null);
    setTimeout(() => inputRef.current?.focus(), 100);

    if (!currentPrefill) return;
    let cancelled = false;
    (async () => {
      // Customer hydration
      if (currentPrefill.customer_id) {
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/customers/${currentPrefill.customer_id}`,
            { credentials: 'include' },
          );
          if (res.ok && !cancelled) {
            const c = await res.json();
            setForm(f => ({
              ...f,
              customer_id: c.customer_id || f.customer_id,
              customer_name: c.display_name || c.name
                || `${c.first_name || ''} ${c.last_name || ''}`.trim()
                || f.customer_name,
              first_name: c.first_name || f.first_name || '',
              last_name: c.last_name || f.last_name || '',
              customer_phone: c.mobile || f.customer_phone || '',
              customer_landline: c.landline || c.phone || f.customer_landline || '',
              customer_email: c.email || f.customer_email || '',
              address_line1: c.address_line1 || f.address_line1 || '',
              address_line2: c.address_line2 || f.address_line2 || '',
              suburb: c.suburb || f.suburb || '',
              state: c.state || f.state || '',
              postcode: c.postcode || f.postcode || '',
              company: c.company || f.company || '',
              abn: c.abn || f.abn || '',
            }));
          }
        } catch (_e) { /* silent — UI still usable with partial prefill */ }
      }
      // Vehicle hydration
      if (currentPrefill.vehicle_id) {
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/vehicles/${currentPrefill.vehicle_id}`,
            { credentials: 'include' },
          );
          if (res.ok && !cancelled) {
            const v = await res.json();
            setForm(f => ({
              ...f,
              vehicle_id: v.vehicle_id || f.vehicle_id,
              vehicle_rego: v.rego || f.vehicle_rego || '',
              vehicle_make: v.make || f.vehicle_make || '',
              vehicle_model: v.model || f.vehicle_model || '',
            }));
          }
        } catch (_e) { /* silent */ }
      }
    })();
    return () => { cancelled = true; };
  }, [open, _prefillCustomerId, _prefillVehicleId]);

  // Fetch pending work for the currently-selected vehicle. Triggered
  // whenever the SM lands on a vehicle (either picked from search OR
  // seeded by `prefill` from another page like Customer Details).
  //
  // iter138.9ao — when the caller passes `prefillPendingItems` (e.g.
  // Invoice → Quote Builder → "Book Customer (3)"), skip the fetch and
  // use those rows directly. This keeps the SM focused on the items they
  // ticked, rather than re-showing the full pending-work history.
  useEffect(() => {
    if (!open) return;
    // Explicit override from caller — seed with their selection.
    if (prefillPendingItems && prefillPendingItems.length > 0) {
      const seeded = prefillPendingItems.map((it, idx) => ({
        pending_id: it.pending_id || `prefilled_${idx}_${Date.now()}`,
        description: it.description,
        category: it.category || 'part_and_labour',
        quantity: it.quantity ?? 1,
        unit: it.unit || 'each',
        parts_price: it.parts_price ?? 0,
        parts_cost_price: it.parts_cost_price ?? 0,
        parts_supplier: it.parts_supplier || '',
        labour_hours: it.labour_hours ?? 0,
        labour_rate: it.labour_rate ?? 0,
        line_total: it.line_total ?? 0,
        source: it.source || 'quote',
        source_invoice_id: it.source_invoice_id,
        source_line_id: it.source_line_id,
        ts: it.ts,
      }));
      setPendingWork(seeded);
      const init = {};
      seeded.forEach(it => { init[it.pending_id] = true; });
      setPendingSelected(init);
      return;
    }
    if (!form.vehicle_id) {
      setPendingWork([]);
      setPendingSelected({});
      return;
    }
    let cancelled = false;
    (async () => {
      setPendingLoading(true);
      try {
        const siteParams = getSiteParams ? getSiteParams() : '';
        const url = `${BACKEND_URL}/api/vehicles/${form.vehicle_id}/pending-work${siteParams}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) { setPendingWork([]); setPendingSelected({}); return; }
        const data = await res.json();
        if (cancelled) return;
        const items = data.items || [];
        setPendingWork(items);
        // Default: every row TICKED — saves the SM clicks on the common
        // case where the customer is calling specifically about these.
        const init = {};
        items.forEach(it => { init[it.pending_id] = true; });
        setPendingSelected(init);
      } catch (_e) {
        if (!cancelled) { setPendingWork([]); setPendingSelected({}); }
      } finally { if (!cancelled) setPendingLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, form.vehicle_id, getSiteParams, prefillPendingItems]);

  // Debounced customer search
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    // iter235 — Mechanic mode: customer search fully disabled (no
    // confidential customer browsing). They type fresh details instead.
    if (hideSearch) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const siteParams = getSiteParams ? getSiteParams() : '';
        const sep = siteParams ? '&' : '?';
        const url = `${BACKEND_URL}/api/bookings/search/customers${siteParams}${sep}query=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || data.customers || (Array.isArray(data) ? data : []));
        }
      } catch (_e) { /* noop */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, getSiteParams, hideSearch]);

  const pickCustomer = (customer, vehicle = null) => {
    setForm(f => ({
      ...f,
      customer_id: customer.customer_id || null,
      customer_name: customer.display_name || customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || f.customer_name,
      first_name: customer.first_name || f.first_name,
      last_name: customer.last_name || f.last_name,
      // iter124: smart-map existing customer → form fields. In the
      // customer DB schema, `customer.mobile` = mobile, `customer.phone`
      // = landline (legacy alias). Map each to the dedicated form
      // field so the SM never sees a landline mis-classified as mobile.
      customer_phone: customer.mobile || customer.mobile_phone || f.customer_phone,
      customer_landline: customer.phone || customer.landline || f.customer_landline,
      customer_email: customer.email || f.customer_email,
      address_line1: customer.address_line1 || customer.address || f.address_line1,
      address_line2: customer.address_line2 || f.address_line2,
      suburb: customer.suburb || f.suburb,
      state: customer.state || f.state,
      postcode: customer.postcode || f.postcode,
      company: customer.company || f.company,
      abn: customer.abn || f.abn,
      vehicle_id: vehicle?.vehicle_id || null,
      vehicle_rego: vehicle?.rego || f.vehicle_rego,
      vehicle_make: vehicle?.make || f.vehicle_make,
      vehicle_model: vehicle?.model || f.vehicle_model,
    }));
    setResults([]);
    setQuery('');
  };

  const toggleService = (t) => {
    setForm(f => ({
      ...f,
      service_types: f.service_types.includes(t)
        ? f.service_types.filter(x => x !== t)
        : [...f.service_types, t],
    }));
  };

  const valid = useMemo(() => {
    // To take a phone booking, the only hard requirements are:
    //  - We can identify the caller (name OR phone)
    //  - We know when they want to come in (date)
    //  - We know what to do (at least 1 service type — already defaults to 'service')
    // Rego, make, model, email, etc. are intentionally optional — service manager
    // can fill them later once the customer arrives.
    if (!form.customer_name.trim() && !form.first_name.trim() && !form.last_name.trim() && !form.customer_phone.trim()) return false;
    if (!form.scheduled_date) return false;
    if (form.service_types.length === 0) return false;
    return true;
  }, [form]);

  const handleSave = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      // Compose proper UTC ISO from Melbourne wall-clock (DST-safe).
      const isoDt = melbourneISO(form.scheduled_date, form.scheduled_time || '09:00');
      const payload = {
        customer_id: form.customer_id || null,
        vehicle_id: form.vehicle_id || null,
        customer_name: (form.customer_name || '').trim()
          || `${form.first_name || ''} ${form.last_name || ''}`.trim(),
        customer_phone: form.customer_phone,
        customer_email: form.customer_email || null,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        customer_landline: form.customer_landline || null,
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        suburb: form.suburb || null,
        state: form.state || null,
        postcode: form.postcode || null,
        company: form.company || null,
        abn: form.abn || null,
        // Rego is optional now — only send when the SM has actually entered one.
        vehicle_rego: form.vehicle_rego.trim() ? form.vehicle_rego.toUpperCase() : null,
        vehicle_make: form.vehicle_make || null,
        vehicle_model: form.vehicle_model || null,
        service_types: form.service_types,
        service_type: form.service_types[0],
        loan_car_requested: !!form.loan_car_requested,
        // iter195 — RWC bookings collect the driver's licence number so the
        // roadworthy cert can be issued. Only sent when RWC is one of the
        // chosen services and a number was entered.
        customer_license_number: (form.service_types.includes('rwc') && form.customer_license_number.trim())
          ? form.customer_license_number.trim().toUpperCase() : null,
        comments: form.notes || null,
        terms_accepted: true,
        booked_datetime: isoDt,
        // Pending work — only include items the SM ticked. Empty array
        // when no vehicle selected or no pending items. Sent as full
        // snapshots so the backend doesn't need to re-fetch them.
        pending_work_items: pendingWork.filter(it => pendingSelected[it.pending_id]),
      };
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const booking = await res.json();
        toast.success(`Booking created — ${booking.booking_number || booking.booking_id}`);
        onCreated?.(booking);
        // Broadcast so any open dashboards / lists can refresh their stats
        // without needing the operator to reload the page.
        try {
          window.dispatchEvent(new CustomEvent('booking-created', { detail: booking }));
        } catch (_e) { /* ignore in non-browser environments */ }
        onClose();
      } else if (res.status === 409) {
        // Duplicate-phone conflict OR duplicate-open-JC. Surface the
        // structured detail so SM can pick "use existing customer" /
        // "open existing JC" with one click instead of re-typing.
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        if (d && typeof d === 'object' && d.code === 'DUPLICATE_PHONE') {
          setPhoneConflict(d);
        } else if (d && typeof d === 'object' && d.code === 'duplicate_open_jc') {
          // iter128 — open JC for same rego+mobile already exists.
          // Drop a sticky toast with the JC number + a link to open it.
          toast.error(
            d.message || `Open JC ${d.existing_job_card_number} already exists for this rego/mobile.`,
            {
              duration: 8000,
              action: d.existing_job_card_id ? {
                label: `Open ${d.existing_job_card_number || 'JC'}`,
                onClick: () => {
                  window.location.href = `/job-cards?focus=${d.existing_job_card_id}`;
                },
              } : undefined,
            }
          );
        } else {
          toast.error(typeof d === 'string' ? d : 'Phone already in use');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to create booking (${res.status})`);
      }
    } catch (_e) {
      toast.error('Failed to create booking');
    } finally { setSaving(false); }
  };

  // iter123: shop name for the SMS preamble. Best-effort fetched from
  // the API on mount; falls back gracefully when the call fails.
  const [shopName, setShopName] = useState('the workshop');
  useEffect(() => {
    if (!open) return;
    fetch(`${BACKEND_URL}/api/sites`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(sites => {
        if (Array.isArray(sites) && sites.length > 0 && sites[0]?.name) {
          setShopName(sites[0].name);
        }
      })
      .catch(() => {});
  }, [open]);

  // iter123: Send Quote SMS preview state. SM clicks "Send Quote" →
  // dialog opens showing the formatted SMS body + checkboxes for
  // mobile/landline. SM reviews → clicks "Send SMS" → backend fires.
  // Modal stays open after the send result lands so SM can either
  // close or follow up with "Create Booking".
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  // Which numbers the SM wants the quote sent to (multi-recipient)
  const [quoteTargets, setQuoteTargets] = useState({ mobile: true, landline: false });

  // Compose the SMS body the SM will see in the preview AND that the BE
  // will send verbatim (when supplied). Keeps WYSIWYG promise — what
  // the SM previews is exactly what the customer receives.
  const composedQuoteBody = useMemo(() => {
    const name = (form.customer_name || `${form.first_name || ''} ${form.last_name || ''}`.trim() || 'Customer').trim();
    const quote = form.notes || '';
    return (
      `Dear ${name},\n\n` +
      `Below is the quote requested:\n${quote}\n\n` +
      `Please confirm your availability so we can book you in.\n\n` +
      `— ${shopName}`
    );
  }, [form.customer_name, form.first_name, form.last_name, form.notes, shopName]);

  // iter122: SM clicks "Send Quote" → opens preview dialog (iter123)
  // instead of immediately firing. Validation happens here so the
  // preview dialog never opens with bad state.
  const handleSendQuote = () => {
    const nameOk = !!(form.customer_name.trim() || form.first_name.trim() || form.last_name.trim());
    const phoneOk = !!(form.customer_phone.trim() || form.customer_landline.trim());
    const notesOk = !!form.notes.trim();
    if (!nameOk) { toast.error('Customer name required to send quote'); return; }
    if (!phoneOk) { toast.error('Mobile/phone required to SMS the quote'); return; }
    if (!notesOk) {
      toast.error('Type the quote in the Notes field — that goes to the customer via SMS');
      return;
    }
    // Default target picker — pre-select whichever number(s) the SM typed
    setQuoteTargets({
      mobile: !!form.customer_phone.trim(),
      landline: !!form.customer_landline.trim() && !form.customer_phone.trim(),
    });
    setShowQuotePreview(true);
  };

  // Actually POST the quote after the SM confirmed the preview.
  const confirmSendQuote = async () => {
    if (sendingQuote) return;
    // Build target list from the checkbox state
    const targets = [];
    if (quoteTargets.mobile && form.customer_phone.trim()) targets.push(form.customer_phone.trim());
    if (quoteTargets.landline && form.customer_landline.trim()) targets.push(form.customer_landline.trim());
    if (targets.length === 0) {
      toast.error('Pick at least one number (mobile or landline) to send to');
      return;
    }
    setSendingQuote(true);
    setQuoteResult(null);
    try {
      const payload = {
        customer_name: (form.customer_name || '').trim()
          || `${form.first_name || ''} ${form.last_name || ''}`.trim(),
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        customer_phone: form.customer_phone || form.customer_landline,
        customer_landline: form.customer_landline || null,
        customer_email: form.customer_email || null,
        vehicle_rego: form.vehicle_rego ? form.vehicle_rego.toUpperCase() : null,
        vehicle_make: form.vehicle_make || null,
        vehicle_model: form.vehicle_model || null,
        address_line1: form.address_line1 || null,
        suburb: form.suburb || null,
        state: form.state || null,
        postcode: form.postcode || null,
        company: form.company || null,
        notes: form.notes,
        // iter123: explicit recipient list + verbatim SMS body
        sms_targets: targets,
        sms_body: composedQuoteBody,
      };
      const res = await fetch(`${BACKEND_URL}/api/bookings/send-quote`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setQuoteResult(data);
        setForm(f => ({ ...f, customer_id: data.customer_id }));
        setShowQuotePreview(false);
        const recipientLabel = targets.length === 1 ? targets[0] : `${targets.length} numbers`;
        if (data.sms_status === 'sent' || data.sms_status === 'dry_run') {
          toast.success(
            data.sms_status === 'dry_run'
              ? `Quote logged (${data.invoice_doc_no}) — SMS gateway not configured yet, message saved to outbox.`
              : `Quote SMS sent to ${recipientLabel} — ${data.invoice_doc_no}.`,
            { duration: 7000 },
          );
        } else {
          toast.warning(`Quote saved (${data.invoice_doc_no}) but SMS failed: ${data.sms_error || 'unknown'}`);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to send quote (${res.status})`);
      }
    } catch (_e) {
      toast.error('Failed to send quote');
    } finally { setSendingQuote(false); }
  };

  // iter129 — Standalone Quote (no booking) flow.
  // 1) SM ticks ONLY the "Quote (phone)" service type
  // 2) Quote builder appears in place of the schedule
  // 3) SM hits "Preview & Send Quote" → opens an SMS preview dialog
  // 4) SM tweaks the auto-generated SMS body → confirm → quote is
  //    created on the server and the SMS is sent. No JC, no booking.
  const validQuoteMode = () => {
    const hasName = !!(form.customer_name.trim() || form.first_name.trim() || form.last_name.trim());
    const hasPhone = !!(form.customer_phone.trim() || form.customer_landline.trim());
    const validRows = quoteRows.filter(r => (r.description || '').trim());
    return hasName && hasPhone && validRows.length > 0;
  };

  const openQuotePreview = () => {
    if (!validQuoteMode()) {
      toast.error('Need name, phone, and at least one quote line');
      return;
    }
    const customerName = form.customer_name.trim() || `${form.first_name} ${form.last_name}`.trim();
    const body = buildQuoteSmsBody({
      rows: quoteRows.filter(r => (r.description || '').trim()),
      customerName,
      vehicleRego: form.vehicle_rego,
    });
    setQuoteModeSmsBody(body);
    setShowQuotePreview(true);
  };

  const confirmSendStandaloneQuote = async () => {
    setCreatingQuote(true);
    try {
      const customerName = form.customer_name.trim() || `${form.first_name} ${form.last_name}`.trim();
      const cleanRows = quoteRows.filter(r => (r.description || '').trim()).map(r => ({
        line_id: r.line_id,
        description: r.description,
        category: r.category,
        quantity: Number(r.quantity || 1),
        parts_supplier: (r.parts_supplier || '').trim() || null,
        parts_cost_price: r.parts_cost_price === '' ? null : Number(r.parts_cost_price),
        parts_price: r.parts_price === '' ? null : Number(r.parts_price),
        parts_margin_pct: r.parts_margin_pct === '' ? null : Number(r.parts_margin_pct),
        labour_hours: r.labour_hours === '' ? null : Number(r.labour_hours),
        labour_rate: r.labour_rate === '' ? null : Number(r.labour_rate),
        line_total: Number(r.line_total || 0),
      }));
      const createRes = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: form.customer_id,
          customer_name: customerName,
          customer_phone: form.customer_phone,
          customer_landline: form.customer_landline,
          customer_email: form.customer_email,
          vehicle_id: form.vehicle_id,
          vehicle_rego: form.vehicle_rego,
          vehicle_make: form.vehicle_make,
          vehicle_model: form.vehicle_model,
          line_items: cleanRows,
          internal_notes: form.notes,
          customer_message: quoteModeSmsBody,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create quote');
        return;
      }
      const quote = await createRes.json();
      const sendRes = await fetch(`${BACKEND_URL}/api/quotes/${quote.quote_id}/send-sms`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: quoteModeSmsBody }),
      });
      if (sendRes.ok) {
        const sd = await sendRes.json();
        toast.success(`Quote ${quote.quote_number} sent — SMS delivered to ${sd.sent_count} number(s)`, {
          duration: 6000,
          action: {
            label: 'Open quote',
            onClick: () => { window.location.href = `/quotes/${quote.quote_id}`; },
          },
        });
        setShowQuotePreview(false);
        onClose?.();
        onCreated?.();
      } else {
        const err = await sendRes.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Quote created but SMS failed — open the quote to retry.', {
          duration: 8000,
          action: {
            label: 'Open quote',
            onClick: () => { window.location.href = `/quotes/${quote.quote_id}`; },
          },
        });
        setShowQuotePreview(false);
      }
    } catch (_e) {
      toast.error('Network error — try again');
    } finally {
      setCreatingQuote(false);
    }
  };



  // SM clicked "Use this customer instead" on the conflict dialog —
  // adopt the existing customer's record and re-submit the booking.
  // Vehicle is intentionally NOT pre-filled here; the SM has already
  // typed a fresh rego/make/model for the booking, so we keep that.
  // The vehicle they search & pick is independent.
  const adoptExistingAndRetry = async () => {
    if (!phoneConflict?.existing_customer) return;
    const c = phoneConflict.existing_customer;
    setForm(prev => ({
      ...prev,
      customer_id: c.customer_id,
      customer_name: c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      first_name: c.first_name || prev.first_name,
      last_name: c.last_name || prev.last_name,
      customer_phone: c.mobile || c.phone || prev.customer_phone,
      customer_landline: c.phone && c.mobile ? c.phone : prev.customer_landline,
      customer_email: c.email || prev.customer_email,
      address_line1: c.address_line1 || c.address || prev.address_line1,
      address_line2: c.address_line2 || prev.address_line2,
      suburb: c.suburb || prev.suburb,
      state: c.state || prev.state,
      postcode: c.postcode || prev.postcode,
      company: c.company || prev.company,
      abn: c.abn || prev.abn,
    }));
    setPhoneConflict(null);
    // Defer the resubmit so React has time to land the form update —
    // handleSave reads from `form` state.
    setTimeout(() => handleSave(), 50);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="!max-w-[80vw] w-[80vw] max-h-[90vh] overflow-y-auto" data-testid="quick-booking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneIncoming className="h-5 w-5 text-emerald-600" />
            New Booking
          </DialogTitle>
          <DialogDescription>
            {hideSearch
              ? 'Enter the customer\u2019s details below to book them in. The booking goes to your workshop and they\u2019ll get a confirmation SMS.'
              : 'Take a booking without leaving what you\u2019re doing. Search by name / phone / rego, or type new details. It becomes a job card on the scheduled date.'}
          </DialogDescription>
        </DialogHeader>

        {/* Search — hidden entirely in mechanic mode (no customer browsing) */}
        {!hideSearch && (
        <div className="space-y-2">
          <Label htmlFor="qb-search" className="text-xs uppercase tracking-wide text-slate-500">Search customer</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="qb-search"
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, phone, or rego — or skip and type details below"
              className="pl-9"
              data-testid="qb-search-input"
            />
            {searching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">searching…</span>
            )}
          </div>
          {results.length > 0 && (
            <div className="border border-slate-200 rounded-md max-h-80 overflow-y-auto bg-white shadow-sm" data-testid="qb-search-results">
              {results.map((c) => {
                const isLead = c.record_type === 'lead';
                // Leads have lead_id; real customers have customer_id. Use
                // either as the React key + testid suffix.
                const rowId = c.customer_id || c.lead_id || `${c.mobile}-${c.display_name}`;
                return (
                <div key={rowId} className="border-b border-slate-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => pickCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50"
                    data-testid={`qb-pick-customer-${rowId}`}
                  >
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-slate-400" />
                      {c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed'}
                      {isLead && (
                        <span
                          className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded uppercase tracking-wide"
                          data-testid={`qb-lead-badge-${rowId}`}
                          title="Prospect — has bookings but no invoice yet"
                        >
                          Lead
                        </span>
                      )}
                      {(c.vehicle_count ?? (c.vehicles || []).length) > 0 && (
                        <span className="text-[10px] font-normal text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          {c.vehicle_count ?? c.vehicles.length} car{(c.vehicle_count ?? c.vehicles.length) === 1 ? '' : 's'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 ml-5">{c.mobile || c.phone || '—'} · {c.email || ''}</p>
                  </button>
                  {(c.vehicles || []).length > 0 && (
                    <div className="bg-slate-50/40 max-h-40 overflow-y-auto" data-testid={`qb-vehicle-list-${rowId}`}>
                      {(c.vehicles || []).map((v, idx) => (
                        <button
                          key={v.vehicle_id || `${rowId}-veh-${idx}-${v.rego}`}
                          type="button"
                          onClick={() => pickCustomer(c, v)}
                          className="w-full text-left pl-10 pr-3 py-1.5 text-xs hover:bg-emerald-50 flex items-center gap-2 text-slate-600"
                          data-testid={`qb-pick-vehicle-${v.vehicle_id || `${rowId}-${v.rego}`}`}
                        >
                          <Car className="h-3 w-3 text-slate-400" />
                          <span className="font-semibold text-slate-900">{v.rego}</span>
                          <span>{v.make} {v.model}</span>
                          {v.year && <span className="text-slate-400">· {v.year}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}
        </div>
        )}

        {/* Customer + Vehicle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Customer
              {form.customer_id && (
                <span className="ml-2 normal-case tracking-normal text-[10px] text-emerald-600">
                  · existing — edits update the customer record
                </span>
              )}
            </Label>
            {/* iter124: removed `Display name` field from booking UI —
                the BE auto-composes it from first+last name. The form
                still carries `customer_name` state and POSTs it for
                backwards-compat. */}
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={form.first_name}
                onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                placeholder="First name *"
                data-testid="qb-customer-first-name"
              />
              <Input
                value={form.last_name}
                onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                placeholder="Last name"
                data-testid="qb-customer-last-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={form.customer_phone}
                onChange={(e) => setForm(f => ({ ...f, customer_phone: formatAUMobile(e.target.value), customer_id: f.customer_id }))}
                placeholder="Mobile *"
                data-testid="qb-customer-phone"
              />
              <Input
                value={form.customer_landline}
                onChange={(e) => setForm(f => ({ ...f, customer_landline: formatAUMobile(e.target.value) }))}
                placeholder="Landline (optional)"
                data-testid="qb-customer-landline"
              />
            </div>
            <Input
              value={form.customer_email}
              onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
              placeholder="Email (optional)"
              data-testid="qb-customer-email"
            />
            <Input
              value={form.address_line1}
              onChange={(e) => setForm(f => ({ ...f, address_line1: e.target.value }))}
              placeholder="Address line 1 (optional)"
              data-testid="qb-customer-addr1"
            />
            {/* iter124: removed `Address line 2`, `Company`, `ABN` — rarely
                used during a phone booking, and they cluttered the form.
                Backend still preserves these fields on the customer
                record (when set elsewhere, e.g. CustomerDetails page). */}
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={form.suburb}
                onChange={(e) => setForm(f => ({ ...f, suburb: e.target.value }))}
                placeholder="Suburb"
                data-testid="qb-customer-suburb"
              />
              <Input
                value={form.state}
                onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))}
                placeholder="State"
                data-testid="qb-customer-state"
              />
              <Input
                value={form.postcode}
                onChange={(e) => setForm(f => ({ ...f, postcode: e.target.value }))}
                placeholder="Postcode"
                data-testid="qb-customer-postcode"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Vehicle <span className="ml-1 normal-case tracking-normal text-[10px] text-slate-400">(optional — can be filled later)</span>
            </Label>
            <Input
              value={form.vehicle_rego}
              onChange={(e) => setForm(f => ({ ...f, vehicle_rego: formatRego(e.target.value), vehicle_id: null }))}
              placeholder="Rego (optional)"
              data-testid="qb-vehicle-rego"
              className="uppercase font-medium"
            />
            <Input
              value={form.vehicle_make}
              onChange={(e) => setForm(f => ({ ...f, vehicle_make: e.target.value, vehicle_id: null }))}
              placeholder="Make"
              data-testid="qb-vehicle-make"
            />
            <Input
              value={form.vehicle_model}
              onChange={(e) => setForm(f => ({ ...f, vehicle_model: e.target.value, vehicle_id: null }))}
              placeholder="Model"
              data-testid="qb-vehicle-model"
            />
          </div>
        </div>

        {/* Pending work — surfaces unresolved findings + un-approved quote
            rows from this vehicle's prior visits. Shows only when an
            EXISTING vehicle is picked (no point on new customers).
            Defaults to all-ticked. Items carry pricing snapshot. */}
        {form.vehicle_id && (
          <PendingWorkPicker
            loading={pendingLoading}
            items={pendingWork}
            selected={pendingSelected}
            onToggle={(pid) => setPendingSelected(prev => ({ ...prev, [pid]: !prev[pid] }))}
            onToggleAll={(all) => {
              const next = {};
              pendingWork.forEach(it => { next[it.pending_id] = all; });
              setPendingSelected(next);
            }}
          />
        )}

        {/* Schedule — hidden in quote-mode (no booking is created) */}
        {!quoteMode && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Date
            </Label>
            <Input
              type="date"
              value={form.scheduled_date}
              onChange={(e) => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
              data-testid="qb-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Time</Label>
            <Input
              type="time"
              value={form.scheduled_time}
              onChange={(e) => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
              data-testid="qb-time"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.loan_car_requested}
                onCheckedChange={(v) => setForm(f => ({ ...f, loan_car_requested: !!v }))}
                data-testid="qb-loan-car"
              />
              Loan car
            </label>
          </div>
        </div>
        )}

        {/* Service types as pill toggles */}
        <div className="mt-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Service Type</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {SERVICE_TYPES.map(t => {
              const active = form.service_types.includes(t.value);
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggleService(t.value)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${active ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`qb-service-${t.value}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* iter195 — RWC (Roadworthy) needs the customer's driver's licence
            number. Only asked when RWC is a chosen service. */}
        {form.service_types.includes('rwc') && (
          <div className="mt-2" data-testid="qb-license-block">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Driver's Licence Number <span className="text-slate-400 normal-case">(for the Roadworthy Certificate)</span>
            </Label>
            <Input
              value={form.customer_license_number}
              onChange={(e) => setForm(f => ({ ...f, customer_license_number: e.target.value }))}
              placeholder="e.g. 012345678"
              className="mt-1 h-9 text-sm uppercase"
              data-testid="qb-license-number"
            />
          </div>
        )}

        {/* iter129 — Quote mode: shows a full quote-builder block in place
            of the schedule. Lines auto-compute line_total based on the
            invoice-quote pricing rules. Supplier + ORP are SM-only. */}
        {quoteMode && (
          <div className="mt-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/40">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wide text-indigo-700">Quote Builder (no booking)</Label>
              <span className="text-[10px] text-slate-500">No JC will be created. Customer gets an SMS with the breakdown.</span>
            </div>
            <QuoteBuilder rows={quoteRows} onChange={setQuoteRows} showOrp={true} />
          </div>
        )}

        {/* Notes */}
        <div className="mt-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Notes / Customer concerns / Phone quote</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. brake noise, service overdue, ETA 9:30am — OR type the verbal quote here & click 'Send Quote' below"
            rows={2}
            data-testid="qb-notes"
          />
        </div>

        {/* Sticky footer — iter122-c: button order is Send Quote → Cancel → Create Booking. */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
            {form.customer_id && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">existing customer</Badge>}
            {form.vehicle_id && <Badge className="bg-blue-100 text-blue-700 text-[10px]">existing vehicle</Badge>}
            {!form.customer_id && (form.customer_name || form.first_name || form.last_name) && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Sparkles className="h-2.5 w-2.5 mr-1 inline" />will create customer</Badge>}
            {quoteResult && (
              <span
                className="text-[10px] font-medium text-emerald-700 flex items-center gap-1"
                data-testid="qb-quote-result-banner"
              >
                ✓ Quote sent — <span className="font-mono">{quoteResult.invoice_doc_no}</span>
                {' · '}
                <span className="text-emerald-600">
                  {quoteResult.sms_status === 'sent' ? 'SMS delivered' : `SMS ${quoteResult.sms_status}`}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {quoteMode ? (
              <>
                <Button
                  onClick={openQuotePreview}
                  disabled={!validQuoteMode()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                  data-testid="qb-quote-mode-preview-btn"
                  title="Open the SMS preview, tweak the body, then send."
                >
                  <PhoneIncoming className="h-4 w-4 mr-1" />
                  Preview &amp; Send Quote
                </Button>
                <Button variant="outline" onClick={onClose} data-testid="qb-cancel">
                  <X className="h-4 w-4 mr-1" />Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={guardComms(handleSendQuote)}
                  disabled={sendingQuote || !form.notes.trim() || !(form.customer_phone.trim() || form.customer_landline.trim()) || !(form.customer_name.trim() || form.first_name.trim() || form.last_name.trim())}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                  data-testid="qb-send-quote-btn"
                  title="Type a name + phone + quote in Notes, then click to SMS the customer and record them under Customers → Quote for follow-up."
                >
                  <PhoneIncoming className="h-4 w-4 mr-1" />
                  {sendingQuote ? 'Sending…' : 'Send Quote'}
                </Button>
                <Button variant="outline" onClick={onClose} data-testid="qb-cancel">
                  <X className="h-4 w-4 mr-1" />Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!valid || saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="qb-save"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? 'Saving…' : 'Create Booking'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Duplicate-phone conflict dialog — surfaced when the typed
          customer_phone is already linked to a different customer in
          our records. Prevents the "John (phone 0416…) saved under RAV"
          footgun the user hit on prod. */}
      <Dialog
        open={!!phoneConflict}
        onOpenChange={(v) => { if (!v) setPhoneConflict(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="qb-phone-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Phone number already in use</DialogTitle>
            <DialogDescription>
              The phone number you entered is already linked to another customer.
              To avoid duplicates, pick the existing customer below — or change the
              phone number if this booking is for a different person.
            </DialogDescription>
          </DialogHeader>
          {phoneConflict?.existing_customer && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="qb-phone-conflict-card">
              <p className="text-sm font-semibold text-slate-900">{phoneConflict.existing_customer.display_name}</p>
              <p className="text-xs text-slate-600 mt-0.5">
                {phoneConflict.existing_customer.mobile || phoneConflict.existing_customer.phone}
                {phoneConflict.existing_customer.email && (
                  <span className="ml-2 text-slate-500">· {phoneConflict.existing_customer.email}</span>
                )}
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                You typed: <span className="font-mono">{phoneConflict.typed_phone}</span>
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setPhoneConflict(null)}
              data-testid="qb-phone-conflict-cancel"
            >
              Change phone / cancel
            </Button>
            <Button
              onClick={adoptExistingAndRetry}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="qb-phone-conflict-adopt"
            >
              Use this customer instead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter123: Send Quote SMS preview dialog. Shows the formatted
          SMS body verbatim + checkboxes for mobile / landline so SM can
          pick one or both numbers. Modal stays open after send so the
          SM can either close or progress to "Create Booking" if the
          customer says yes during the call. */}
      <Dialog
        open={showQuotePreview}
        onOpenChange={(v) => { if (!v) setShowQuotePreview(false); }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="qb-quote-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneIncoming className="h-5 w-5 text-indigo-600" />
              Send Quote — preview before SMS
            </DialogTitle>
            <DialogDescription>
              Review the message the customer will receive. Pick the number(s) to send to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Message preview {quoteMode && <span className="text-emerald-600 text-[10px]">(editable)</span>}</Label>
              <Textarea
                value={quoteMode ? quoteModeSmsBody : composedQuoteBody}
                onChange={quoteMode ? (e) => setQuoteModeSmsBody(e.target.value) : undefined}
                readOnly={!quoteMode}
                rows={quoteMode ? 13 : 9}
                className={`font-mono text-xs ${quoteMode ? 'bg-white' : 'bg-slate-50'}`}
                data-testid="qb-quote-preview-body"
              />
              {!quoteMode && (
                <p className="text-[10px] text-slate-500 mt-1">
                  Editing? Close this dialog, tweak the <span className="font-semibold">Notes</span> field on the booking form, then click Send Quote again.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Send to</Label>
              <div className="mt-1.5 space-y-1.5">
                {form.customer_phone.trim() ? (
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="qb-quote-preview-target-mobile">
                    <input
                      type="checkbox"
                      checked={quoteTargets.mobile}
                      onChange={(e) => setQuoteTargets(t => ({ ...t, mobile: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <span className="font-medium text-slate-700">Mobile</span>
                    <span className="font-mono text-xs text-slate-600">{form.customer_phone}</span>
                  </label>
                ) : (
                  <p className="text-[11px] text-slate-400 italic">No mobile typed in the form.</p>
                )}
                {form.customer_landline.trim() ? (
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="qb-quote-preview-target-landline">
                    <input
                      type="checkbox"
                      checked={quoteTargets.landline}
                      onChange={(e) => setQuoteTargets(t => ({ ...t, landline: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <span className="font-medium text-slate-700">Landline</span>
                    <span className="font-mono text-xs text-slate-600">{form.customer_landline}</span>
                  </label>
                ) : (
                  <p className="text-[11px] text-slate-400 italic">No landline typed.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowQuotePreview(false)}
              disabled={sendingQuote}
              data-testid="qb-quote-preview-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={quoteMode ? confirmSendStandaloneQuote : confirmSendQuote}
              disabled={quoteMode ? (creatingQuote || !quoteModeSmsBody.trim()) : (sendingQuote || (!quoteTargets.mobile && !quoteTargets.landline))}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="qb-quote-preview-confirm"
            >
              <PhoneIncoming className="h-4 w-4 mr-1" />
              {quoteMode
                ? (creatingQuote ? 'Sending…' : 'Confirm & Send Quote')
                : (sendingQuote ? 'Sending…' : 'Send SMS now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {commsDialog}
    </Dialog>
  );
}
